import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';

const COLORS = [
  'var(--color-primary)',
  'var(--color-success)',
  'var(--color-warning)',
  'var(--color-accent)',
  '#8b5cf6',
  '#ec4899',
];

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const item = payload[0];
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-elevation-2">
        <p className="text-sm font-medium text-foreground">{item.name}</p>
        <p className="text-xs text-muted-foreground">{item.payload.percentage}% of total</p>
        <p className="text-xs text-muted-foreground">{item.value} properties</p>
      </div>
    );
  }
  return null;
};

const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  if (percent < 0.05) return null;
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight="600">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const PropertyTypeDistribution = ({ data = [], className = '' }) => {
  const chartData = data.map(item => ({ name: item.type, value: item.count, percentage: item.percentage }));
  const total = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <div className={`bg-card rounded-lg border border-border p-6 ${className}`}>
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="PieChart" size={20} color="var(--color-primary)" />
        <h3 className="text-lg font-semibold text-foreground">Property Type Distribution</h3>
      </div>

      {chartData.length === 0 ? (
        <div className="flex items-center justify-center h-80 text-muted-foreground text-sm">No data available</div>
      ) : (
        <>
          <div className="w-full h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={chartData} cx="50%" cy="50%" labelLine={false} label={renderCustomLabel} outerRadius={100} dataKey="value">
                  {chartData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            {chartData.map((item, index) => (
              <div key={item.name} className="flex items-center space-x-3 p-3 bg-muted rounded-md">
                <div className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{item.percentage}% of surveys</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-border grid grid-cols-2 gap-4 text-center">
            <div>
              <p className="text-lg font-bold text-foreground">{total}</p>
              <p className="text-xs text-muted-foreground">Total Properties Surveyed</p>
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">
                {data[0]?.total_portfolio_value ? `₹${Number(data[0].total_portfolio_value).toLocaleString()}` : '—'}
              </p>
              <p className="text-xs text-muted-foreground">Total Portfolio Value</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PropertyTypeDistribution;
